import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, COMPANY } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Lock, LogOut, Users, Inbox, Trophy, Loader2, RefreshCw, Search, Download } from "lucide-react";

const KEY_STORAGE = "ffg_admin_key";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_STYLE = {
  new: "text-primary",
  contacted: "text-blue-600",
  quoted: "text-amber-600",
  won: "text-green-600",
  lost: "text-muted-foreground",
};
const LANG_LABEL = { en: "English", es: "Español", ht: "Kreyòl", pt: "Português" };
const SOURCE_LABEL = { givehub: "GiveHub", merchant: "Merchant", website: "Website" };
const SOURCE_STYLE = {
  givehub: "bg-purple-100 text-purple-700",
  merchant: "bg-primary/10 text-primary",
  website: "bg-slate-100 text-slate-600",
};

export default function AdminPage() {
  const [key, setKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, won: 0 });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const load = useCallback(async (adminKey) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Key": adminKey };
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(l.data);
      setStats(s.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(KEY_STORAGE);
        setAuthed(false);
        setKey("");
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Failed to load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const token = res.data.token;
      localStorage.setItem(KEY_STORAGE, token);
      setKey(token);
      toast.success("Welcome back!");
    } catch {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setKey("");
    setAuthed(false);
    setPassword("");
  };

  const updateStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, { headers: { "X-Admin-Key": key } });
      toast.success("Status updated");
      // refresh stats
      const s = await axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Key": key } });
      setStats(s.data);
    } catch {
      toast.error("Could not update status");
      load(key);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (sourceFilter !== "all" && (l.source || "website") !== sourceFilter) return false;
      if (!q) return true;
      return [l.name, l.business_name, l.email, l.phone, l.message]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [leads, query, filter, sourceFilter]);

  const exportCSV = () => {
    const headers = ["Name", "Business", "Email", "Phone", "Monthly Volume", "Source", "Language", "Status", "Message", "Date"];
    const rows = filtered.map((l) => [
      l.name, l.business_name, l.email, l.phone, l.monthly_volume,
      SOURCE_LABEL[l.source] || l.source || "Website",
      LANG_LABEL[l.language] || l.language || "English", l.status,
      (l.message || "").replace(/\n/g, " "), new Date(l.created_at).toLocaleString(),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-secondary px-6">
        <Card className="w-full max-w-sm p-8">
          <div className="mb-6 flex flex-col items-center text-center">
            <img src={COMPANY.logo} alt={COMPANY.legalName} className="mb-4 h-10 w-auto" />
            <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
              <Lock className="h-6 w-6" />
            </span>
            <h1 className="font-display text-2xl font-semibold">Admin Back Office</h1>
            <p className="mt-1 text-sm text-muted-foreground">Leads Dashboard</p>
          </div>
          <form onSubmit={login} className="space-y-4">
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button data-testid="admin-login-button" type="submit" disabled={loading} className="h-11 w-full font-semibold">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log In"}
            </Button>
          </form>
          <a href="/" className="mt-4 block text-center text-sm text-muted-foreground hover:text-foreground">
            ← Back to site
          </a>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-4">
            <img src={COMPANY.logo} alt={COMPANY.legalName} className="h-8 w-auto" />
            <div className="hidden sm:block">
              <h1 className="font-display text-lg font-semibold leading-tight">Back Office</h1>
              <p className="text-xs text-muted-foreground">Customer leads</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="admin-export-button" variant="outline" onClick={exportCSV} disabled={!filtered.length}>
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button data-testid="admin-refresh-button" variant="outline" onClick={() => load(key)} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="admin-logout-button" variant="outline" onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {/* Stats */}
        <div className="mb-6 grid grid-cols-3 gap-4 sm:max-w-xl">
          <StatCard testid="stat-total" icon={Users} tone="primary" value={stats.total} label="Total leads" />
          <StatCard testid="stat-new" icon={Inbox} tone="accent" value={stats.new} label="New" />
          <StatCard testid="stat-won" icon={Trophy} tone="green" value={stats.won} label="Won" />
        </div>

        {/* Controls */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="admin-search"
              placeholder="Search by name, business, email, phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-status-filter" className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="admin-source-filter" className="w-full sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="merchant">Merchant</SelectItem>
              <SelectItem value="givehub">GiveHub</SelectItem>
              <SelectItem value="website">Website</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="overflow-hidden">
          {filtered.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground" data-testid="empty-leads">
              <Inbox className="mx-auto mb-3 h-10 w-10 opacity-40" />
              {leads.length === 0 ? "No leads yet. Submissions from your landing page will appear here." : "No leads match your filters."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Volume</TableHead>
                    <TableHead>Lang</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="font-medium">{l.name}</TableCell>
                      <TableCell>{l.business_name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${SOURCE_STYLE[l.source] || SOURCE_STYLE.website}`}>
                          {SOURCE_LABEL[l.source] || "Website"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <a href={`mailto:${l.email}`} className="block text-primary hover:underline">{l.email}</a>
                        <a href={`tel:${l.phone}`} className="block text-sm text-muted-foreground hover:underline">{l.phone}</a>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">{l.monthly_volume}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{LANG_LABEL[l.language] || "English"}</TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm" title={l.message}>{l.message || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {new Date(l.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger data-testid={`status-select-${l.id}`} className={`h-9 w-32 font-medium capitalize ${STATUS_STYLE[l.status] || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
        <p className="mt-3 text-sm text-muted-foreground">Showing {filtered.length} of {leads.length} leads</p>
      </main>
    </div>
  );
}

function StatCard({ testid, icon: Icon, tone, value, label }) {
  const tones = {
    primary: "bg-primary/10 text-primary",
    accent: "bg-accent/10 text-accent",
    green: "bg-green-100 text-green-600",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="font-display text-2xl font-semibold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </div>
    </Card>
  );
}
